import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useChangeTitle from "src/api/hooks/useChangeTitle";
import useCreateVariation from "src/api/hooks/useCreateVariation";
import useFileMutation from "src/api/hooks/useFileMutation";
import { useForm } from "src/api/hooks/useForm";
import useGroups from "src/api/hooks/useGroups";
import useMyUser from "src/api/hooks/useMyUser";
import useProjectVariations from "src/api/hooks/useProjectVariations";
import { queryKeys } from "src/api/queryKeys";
import { objectTypes } from "src/consts/objectTypes";
import { lang } from "src/language/Strings";
import { routes } from "src/routes";
import { payloadType } from "src/types/InspectionPayloadType";
import { LocalStorageKeys } from "src/utils/localStorageKeys";
import CheckDeviationData from "../CheckDeviationData";
import FormFieldsWrapper from "./FormFieldsWrapper";
import { usePosition } from "src/api/hooks/usePosition";
import { InspectionResponse } from "src/types/InspectionUpdateRequest";
import { useStore } from "src/components/Store";
export default function CreateInspectionForm({
  formFields,
  objectType,
}: {
  objectType: keyof typeof objectTypes;
  formFields: (keyof payloadType)[];
}) {
  const { companyId, inspectionId } = useParams();
  const create = useCreateVariation();
  const { create: createFile } = useFileMutation();
  const queryClient = useQueryClient();
  const position = usePosition();
  const { data: myUser } = useMyUser();
  const [checkDataOpen, setCheckDataOpen] = useState<boolean>(false);
  const { data: inspections } = useProjectVariations();
  const store = useStore();
  useChangeTitle(
    objectType === objectTypes.Deviation
      ? lang.createDeviation
      : lang.createInspection
  );

  const { Form, formData, formRef } = useForm<payloadType>(
    {
      status: 1,
      title:
        inspections?.find((i) => i.id === Number(inspectionId))?.title || "",
      description: "",
      externalId: store.externalObjectId ? store.externalObjectId : "",
      customer:
        inspections?.find((i) => i.id === Number(inspectionId))?.title || "",
      userId: myUser?.id,
      deviationType: 1,
      companyId: Number(companyId),
      parentDeviationId: inspectionId ? Number(inspectionId) : undefined,
      groupId:
        inspections?.find((i) => i.id === Number(inspectionId))?.groupId ||
        Number(localStorage.getItem(LocalStorageKeys.groupId)) ||
        myUser?.groups?.[0]?.id ||
        0,
      files: [],
      latitude: position.latitude || 0,
      longitude: position.longitude || 0,
      objectType: objectType,
      information:
        inspections?.find((i) => i.id === Number(inspectionId))?.information || 
        localStorage.getItem(LocalStorageKeys.defaultDeviationInfo) ||
        "",
    },
    inspections as payloadType[]
  );
  const { data: groups } = useGroups();

  const onSubmitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCheckDataOpen(false);
    const groupId = Number(
      localStorage.getItem("groupId") ??
        myUser?.groups?.[0]?.id ??
        groups?.[0]?.id
    );
    const payload = {
      ...formData,
      title: formData.title || store.externalObjectId || "-",
      groupId: groupId,
      latitude: position.latitude || 0,
      longitude: position.longitude || 0,
      userId: myUser?.id,
    };
    setDeviationPayload(payload);
  };

  const [deviationPayload, setDeviationPayload] =
    useState<payloadType | null>();

  const handleCoordinatesChange = (longitude: number, latitude: number) => {
    if (deviationPayload) {
      setDeviationPayload({
        ...deviationPayload,
        longitude,
        latitude,
      });
    }
  };

  const saveData = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (deviationPayload) {
      create.mutate(deviationPayload);

      if (formRef.current) {
        formRef.current.reset();
      }
    }
  };

  const navigate = useNavigate();
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );
  const handleUploadProgress = useCallback(
    (progress: number, fileName: string) => {
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        newProgress[fileName] = progress;
        return newProgress;
      });
    },
    []
  );

  useEffect(() => {
    const plusElement = document.getElementById("plus-icon-add");
    const plusElementHandler = () => {
      navigate(
        routes.Egenkontroller.Pages.Deviations.Create.url(
          companyId,
          inspectionId
        )
      );
    };
    if (plusElement) {
      plusElement.addEventListener("click", plusElementHandler);
    }
    return () => {
      plusElement?.removeEventListener("click", plusElementHandler);
      store.setExternalObjectId("")
      store.setDefaultInspectionComment("")
    };
  }, []);

  useEffect(() => {
    if (create.status === "success") {
      queryClient.invalidateQueries({
        queryKey: queryKeys.variations(companyId),
      });
      deviationPayload!.files!.forEach((file) => {
        file.deviationId = create.data?.id;
        file.companyId = Number(companyId);
        if (file.id) {
          createFile.mutate(file);
        }
      });
      setCheckDataOpen(false);
      if (objectType === objectTypes.Deviation && inspectionId) {
        navigate(
          routes.Egenkontroller.Pages.Deviations.ShowById.url(
            companyId,
            inspectionId,
            create.data?.id
          )
        );
      } else if (objectType === objectTypes.Deviation) {
        navigate(routes.Deviation.Pages.Show.url(companyId, create.data?.id));
      } else {
        navigate(
          routes.Egenkontroller.Pages.Show.url(companyId, create.data?.id)
        );
      }
    }

    if (create.status === "error") {
      console.error(create.error);
      navigate(routes.Egenkontroller.Pages.List.url(companyId));
    }
  }, [create.status]);

  return (
    <main className="flex flex-grow text-gray-900 flex-col">
      <div className={"w-full justify-center flex"}>
        <Form
          onSubmit={onSubmitHandler}
          className="w-[80%] max-w-[80%] relative z-0"
          autoComplete="on"
          id="createForm"
        >
          {formFields.includes("title") && (
            <FormFieldsWrapper label={lang.address}>
              <Form.Input
                name="title"
                id="title"
                type="text"
                key={
                  inspections?.find((i) => i.id === Number(inspectionId))?.title
                }
                defaultValue={
                  store.externalObjectId ? store.externalObjectId : inspectionId
                    ? inspections?.find((i) => i.id === Number(inspectionId))
                        ?.title
                    : "abcde"
                }
                autoComplete="off"
                required
                className="border-2 border-Grey9 rounded-lg pt-2 pb-2 "
              />
            </FormFieldsWrapper>
          )}
          {formFields.includes("deviationType") && (
            <FormFieldsWrapper label={lang.deviationType}>
              <Form.Select
                id="deviationType"
                title={lang.deviationType}
                name="deviationType"
                required
                className="border-2 border-Grey9 rounded-lg pt-2 pb-2 "
              >
                <Form.Select.Option value={1}>
                  {lang.improvement}
                </Form.Select.Option>
                <Form.Select.Option value={2}>{lang.urgent}</Form.Select.Option>
                <Form.Select.Option value={3}>
                  {lang.vandalism}
                </Form.Select.Option>
                <Form.Select.Option value={4}>{lang.other}</Form.Select.Option>
              </Form.Select>
            </FormFieldsWrapper>
          )}
          {formFields.includes("customer") && (
            <FormFieldsWrapper label={lang.customer}>
              <datalist id="customer-list" className="hidden">
                {inspections
                  ?.reduce((acc: InspectionResponse[], obj) => {
                    if (!acc.some((o) => o.customer === obj.customer)) {
                      acc.push(obj);
                    }
                    return acc;
                  }, [])
                  .map((inspection) => (
                    <option
                      key={inspection.id}
                      value={inspection.customer || ""}
                    />
                  ))}
              </datalist>
              <Form.Input
                name="customer"
                id="customer"
                type="text"
                list="customer-list"
                autoComplete="off"
                className="border-2 border-Grey9 rounded-lg pt-2 pb-2 "
                required
              />
            </FormFieldsWrapper>
          )}
          {formFields.includes("information") && (
            <FormFieldsWrapper label={lang.moment}>
              <datalist id="information-list" className="hidden">
                {inspections
                  ?.reduce((acc: InspectionResponse[], obj) => {
                    if (!acc.some((o) => o.information === obj.information)) {
                      acc.push(obj);
                    }
                    return acc;
                  }, [])
                  .map((inspection) => (
                    <option
                      key={inspection.id}
                      value={inspection.information || ""}
                    />
                  ))}
              </datalist>
              <Form.Input
                name="information"
                id="information"
                list="information-list"
                enterKeyHint="next"
                autoComplete="off"
                className="border-2 border-Grey9 rounded-lg pt-2 pb-2 "
                required
              />
            </FormFieldsWrapper>
          )}

          {formFields.includes("description") && (
            <FormFieldsWrapper label={lang.description}>
              <Form.TextArea
                name="description"
                id="description"
                rows={6}
                autoComplete="off"
                className="border-2 border-Grey9 rounded-lg pt-2 pb-2 "
                required
              />
            </FormFieldsWrapper>
          )}

          {formFields.includes("files") && (
            <FormFieldsWrapper label={lang.addImages}>
              <Form.FileInput
                onUploadProgress={handleUploadProgress}
                name="files"
                id="files"
              />
            </FormFieldsWrapper>
          )}
          <div className="flex flex-col items-center">
            {position.fromIp ||
            position.latitude === 0 ||
            position.longitude === 0 ? (
              <small className="text-PrimaryRed">
                {lang.locationNotEnabled}
              </small>
            ) : null}
            <button
              type="submit"
              id="submit"
              className="mt-4 p-2 bg-[#1F4652] text-White rounded-lg disabled:bg-[#1F4652]/50 disabled:opacity-50"
              disabled={
                Object.values(uploadProgress).some(
                  (progress) => progress !== 1
                ) ||
                Object.keys(formData).some((key) => {
                  if (!formFields.includes(key as keyof payloadType)) {
                    return false;
                  }
                  return ["", undefined, null].includes(
                    formData[key as keyof payloadType] as
                      | string
                      | undefined
                      | null
                  );
                })
              }
            >
              {lang.save}
            </button>
          </div>
        </Form>
      </div>
      {checkDataOpen && deviationPayload && (
        <CheckDeviationData
          deviationPayload={deviationPayload}
          setCheckDataOpen={setCheckDataOpen}
          saveData={saveData}
          onCoordinatesChange={handleCoordinatesChange}
          setDeviationPayload={setDeviationPayload}
        />
      )}
    </main>
  );
}
